import { useEffect, useReducer, useRef, useState } from "react";
import { createMediaClient, type MediaClient } from "../../services/mediaClient";
import { type MediaDevicePreferences } from "../../services/mediaDevicePreferences";
import { MediaUnavailableError, mediaSupportCode } from "../../services/mediaSupport";
import { screenShareReducer, initialScreenShare, canStartShare, isScreenShare, shouldShowScreenPane, isSelfSharing, SELF } from "./screenShareState";
import { cameraStageDirection, deriveOrientation, type Orientation } from "./callLayout";
import { useI18n } from "../../i18n/localization";
import { MaterialMediaIcon } from "./MaterialMediaIcon";

// Track portrait/landscape from the viewport so the two-camera split can flip between a
// vertical (portrait) and horizontal (landscape) 50/50 layout. SSR-safe (defaults landscape).
function useOrientation(): Orientation {
  const read = (): Orientation =>
    typeof window === "undefined" ? "landscape" : deriveOrientation(window.innerWidth, window.innerHeight);
  const [orientation, setOrientation] = useState<Orientation>(read);
  useEffect(() => {
    const onChange = () => setOrientation(read());
    window.addEventListener("resize", onChange);
    window.addEventListener("orientationchange", onChange);
    return () => {
      window.removeEventListener("resize", onChange);
      window.removeEventListener("orientationchange", onChange);
    };
  }, []);
  return orientation;
}

// Fullscreen on the call root, working on desktop and mobile. Reports `supported: false`
// where the Fullscreen API is unavailable (e.g. iOS Safari on a non-video element), so the
// button can hide gracefully.
function useFullscreen(targetRef: React.RefObject<HTMLElement | null>) {
  const supported = typeof document !== "undefined" && document.fullscreenEnabled === true;
  const [isFullscreen, setIsFullscreen] = useState(false);
  useEffect(() => {
    const onChange = () => setIsFullscreen(document.fullscreenElement != null);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);
  const toggle = () => {
    if (!supported) return;
    if (document.fullscreenElement) {
      void document.exitFullscreen().catch(() => undefined);
    } else if (targetRef.current?.requestFullscreen) {
      void targetRef.current.requestFullscreen().catch(() => undefined);
    }
  };
  return { supported, isFullscreen, toggle };
}

// Turn a thrown error into a user-facing Hebrew message:
//   MediaUnavailableError  → VC-MEDIA-004 (no HTTPS) or VC-MEDIA-001 (API missing)
//   NotAllowedError        → VC-MEDIA-001 (permission denied by user or browser policy)
//   other DOMException     → VC-SYS-001  (device in use, no device found, etc.)
//   anything else          → raw .toString() as a last resort
function toErrorMessage(e: unknown, messageFor: (code: string) => string): string {
  if (e instanceof MediaUnavailableError) return messageFor(mediaSupportCode(e.support));
  if (e instanceof DOMException) {
    if (e.name === "NotAllowedError") return messageFor("VC-MEDIA-001");
    return messageFor("VC-SYS-001");
  }
  return String(e);
}

// In-call split-screen hearing (docs/design/05-uiux-spec.md §3.5). Driven by the
// call-flow container: callId/peerId/mediaToken come from the SignalR callAccepted
// event (modules/05). Renders the traffic officer beside the field officer + citizen,
// with mute/camera/end controls, a recording indicator and a reconnect banner.
export function CallScreen({
  callId,
  peerId,
  mediaToken,
  prefs,
  reconnecting,
  onEnd
}: {
  callId: number;
  peerId: string;
  mediaToken: string;
  prefs?: MediaDevicePreferences;
  reconnecting?: boolean;
  onEnd: () => void;
}) {
  const { t, dir, messageFor } = useI18n();
  const rootRef = useRef<HTMLDivElement>(null);
  const localRef = useRef<HTMLVideoElement>(null);
  const remoteRef = useRef<HTMLVideoElement>(null);
  const screenRef = useRef<HTMLVideoElement>(null);
  const orientation = useOrientation();
  const fullscreen = useFullscreen(rootRef);
  // Streams are held in refs and (re)attached to their <video> elements by effects below.
  // Assigning srcObject only inside the media callbacks is not enough: a layout change
  // (e.g. entering screen-share view) remounts the <video> elements, and the new element
  // would have no srcObject — showing a black panel. The effects re-assert the binding.
  const localStreamRef = useRef<MediaStream | null>(null);
  const remoteStreamRef = useRef<MediaStream | null>(null);
  const screenStreamRef = useRef<MediaStream | null>(null);
  const clientRef = useRef<MediaClient | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Initial mic/camera state mirrors the readiness choice so the OSD buttons match the tracks.
  const [micOn, setMicOn] = useState(prefs?.micEnabled ?? true);
  const [camOn, setCamOn] = useState(prefs?.cameraEnabled ?? true);
  const [screen, dispatchScreen] = useReducer(screenShareReducer, initialScreenShare);

  useEffect(() => {
    const remoteStream = new MediaStream();
    const screenStream = new MediaStream();
    remoteStreamRef.current = remoteStream;
    screenStreamRef.current = screenStream;

    const client = createMediaClient({
      onLocalStream: (stream) => {
        localStreamRef.current = stream;
        if (localRef.current) localRef.current.srcObject = stream;
      },
      onRemoteTrack: (rPeerId, track, _kind, appData) => {
        // A shared screen must render in its OWN element: a <video> bound to a stream
        // with two video tracks only shows one (the camera), so mixing the screen track
        // into the camera stream hides it. Route it to screenStream; the reattach effect
        // below binds it once the screen pane mounts (avoids the ref-null race).
        if (isScreenShare(appData)) {
          screenStream.addTrack(track);
          dispatchScreen({ type: "remoteStart", peerId: rPeerId });
          return;
        }
        remoteStream.addTrack(track);
        if (remoteRef.current) remoteRef.current.srcObject = remoteStream;
      },
      // The remote producer closed (sharer stopped). Clear the screen pane via the
      // canonical consumerClosed signal — track "ended" is not always reliable.
      onRemoteTrackClosed: (cPeerId, appData) => {
        if (!isScreenShare(appData)) return;
        screenStream.getTracks().forEach((t) => screenStream.removeTrack(t));
        dispatchScreen({ type: "remoteStop", peerId: cPeerId });
      },
      // Browser-native "Stop sharing" ended our local share. Return the button.
      onLocalScreenShareEnded: () => dispatchScreen({ type: "localStop" }),
      onPeerClosed: (cPeerId) => {
        dispatchScreen({ type: "remoteStop", peerId: cPeerId });
        remoteStream.getTracks().forEach((t) => remoteStream.removeTrack(t));
        screenStream.getTracks().forEach((t) => screenStream.removeTrack(t));
      }
    });
    clientRef.current = client;
    client.start({
      token: mediaToken, callId: String(callId), peerId,
      cameraDeviceId: prefs?.cameraDeviceId, micDeviceId: prefs?.micDeviceId,
      cameraEnabled: prefs?.cameraEnabled ?? true, micEnabled: prefs?.micEnabled ?? true
    }).catch((e: unknown) => setError(toErrorMessage(e, messageFor)));
    return () => {
      void client.stop();
      clientRef.current = null;
      remoteStreamRef.current = null;
      screenStreamRef.current = null;
    };
    // prefs is captured once at call start (same lifetime as mediaToken); it never changes
    // mid-call, so it is intentionally not a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callId, peerId, mediaToken]);

  // Reattach streams to their <video> elements whenever the layout changes (e.g. the
  // screen-share pane mounts/unmounts). Without this, a video element that is newly
  // rendered (or re-rendered) after a layout state change has no srcObject → black panel.
  useEffect(() => {
    if (localRef.current && localStreamRef.current) localRef.current.srcObject = localStreamRef.current;
  });
  useEffect(() => {
    if (remoteRef.current && remoteStreamRef.current) remoteRef.current.srcObject = remoteStreamRef.current;
  });
  useEffect(() => {
    if (screenRef.current && screenStreamRef.current && shouldShowScreenPane(screen))
      screenRef.current.srcObject = screenStreamRef.current;
  }, [screen.sharing, screen.sharedBy]);

  const toggleMic = () => {
    const next = !micOn;
    setMicOn(next);
    clientRef.current?.toggleMic(next);
  };
  const toggleCam = () => {
    const next = !camOn;
    setCamOn(next);
    clientRef.current?.toggleCamera(next);
  };
  const toggleScreen = async () => {
    const client = clientRef.current;
    if (!client) return;
    if (screen.sharedBy === SELF) {
      await client.stopScreenShare();
      dispatchScreen({ type: "localStop" });
    } else if (canStartShare(screen)) {
      try {
        await client.startScreenShare();
        dispatchScreen({ type: "localStart" });
      } catch (e) {
        setError(toErrorMessage(e, messageFor));
      }
    }
  };

  return (
    // flex:1 + min-height:0 + overflow:hidden makes the call screen fill exactly the height
    // the shell gives it (never taller than the viewport) and clip internally, so the call
    // page never scrolls and the OSD controls are always reachable. This div is the
    // fullscreen target.
    <div ref={rootRef} style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", background: "#0f1e3a", overflow: "hidden" }}>
      {reconnecting && (
        <div style={{ background: "#b45309", color: "#fff", padding: "6px 16px", fontSize: 13, textAlign: "center" }}>
          {t.banner.reconnecting}
        </div>
      )}
      <div style={{ flex: 1, minHeight: 0, position: "relative", display: "flex", gap: 4, padding: 4, overflow: "hidden" }}>
        {/* Camera panels are always mounted in this container so their <video> nodes never
            leave the DOM — refs stay stable and srcObject is never reset to null on a
            layout transition. Switching between side-by-side (no remote share) and a column
            (remote share active) is done purely via CSS flexDirection, not by moving nodes. */}
        <div style={{ flex: 1, display: "flex", flexDirection: cameraStageDirection(orientation, shouldShowScreenPane(screen)), gap: 4, minWidth: 0, minHeight: 0 }}>
          <Panel label={t.call.officerPanel} videoRef={remoteRef} labelSide="start" />
          <Panel
            label={t.call.selfPanel}
            videoRef={localRef}
            muted
            recording
            labelSide="end"
            screenSharing={isSelfSharing(screen)}
            recordingLabel={t.banner.recordingOn}
            screenSharingLabel={t.call.screenSharing}
          />
        </div>

        {/* Screen-share pane: only in the DOM while a remote peer is sharing (flex:3 beside
            the camera column). When the local user is sharing there is no inbound screen
            track, so we skip this pane entirely — both camera panels stay full-size and a
            badge on the self panel signals that sharing is active. */}
        {shouldShowScreenPane(screen) && (
          <div style={{ flex: 3, position: "relative", background: "#000", borderRadius: 8, overflow: "hidden" }}>
            <video ref={screenRef} autoPlay playsInline style={{ width: "100%", height: "100%", objectFit: "contain", background: "#000" }} />
            <span style={{ position: "absolute", top: 8, insetInlineStart: 8, color: "#fff", fontSize: 13, background: "rgba(0,0,0,.5)", padding: "2px 8px", borderRadius: 4 }}>
              {t.call.screenSharing}
            </span>
          </div>
        )}

        {/* Error surfaced as an OSD banner over the stage (no opaque toolbar). */}
        {error && (
          <div
            role="alert"
            style={{
              position: "absolute", top: 12, insetInlineStart: 0, insetInlineEnd: 0,
              display: "flex", justifyContent: "center", pointerEvents: "none"
            }}
          >
            <span style={{ background: "rgba(185,28,28,0.9)", color: "#fff", padding: "6px 14px", borderRadius: 8, fontSize: 14, maxWidth: "90%" }}>
              {error}
            </span>
          </div>
        )}

        {/* In-call controls as transparent OSD action buttons over the call view. */}
        <div
          role="toolbar"
          aria-label={t.call.controlsLabel}
          dir={dir}
          style={{
            position: "absolute", insetInlineStart: 0, insetInlineEnd: 0, bottom: 20,
            display: "flex", gap: 12, justifyContent: "center", pointerEvents: "none"
          }}
        >
          <OsdButton
            onClick={toggleMic}
            label={micOn ? t.call.mute : t.call.unmute}
            icon={micOn ? "mic" : "mic_off"}
          />
          <OsdButton
            onClick={toggleCam}
            label={camOn ? t.call.camOff : t.call.camOn}
            icon={camOn ? "video_camera_front" : "video_camera_front_off"}
          />
          <OsdButton
            onClick={toggleScreen}
            disabled={screen.sharing && screen.sharedBy !== SELF}
            label={screen.sharedBy === SELF ? t.call.stopShare : t.call.shareScreen}
            icon={screen.sharedBy === SELF ? "stop_screen_share" : "share_windows"}
          />
          {fullscreen.supported && (
            <OsdButton
              onClick={fullscreen.toggle}
              label={fullscreen.isFullscreen ? t.call.exitFullscreen : t.call.fullscreen}
              icon={fullscreen.isFullscreen ? "fullscreen_exit" : "fullscreen"}
            />
          )}
          <OsdButton onClick={onEnd} label={t.call.endCall} icon="call_end" variant="danger" />
        </div>
      </div>
    </div>
  );
}

// A transparent on-screen-display action button rendered over the video stage. Keeps the
// Hebrew label visible and also exposes it as the accessible name + tooltip.
function OsdButton({
  onClick,
  label,
  icon,
  disabled,
  variant
}: {
  onClick: () => void;
  label: string;
  icon: React.ComponentProps<typeof MaterialMediaIcon>["name"];
  disabled?: boolean;
  variant?: "danger";
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      style={{
        ...osdBtn,
        ...(variant === "danger" ? osdDanger : null),
        ...(disabled ? osdDisabled : null)
      }}
    >
      <MaterialMediaIcon name={icon} size={26} />
    </button>
  );
}

function Panel({
  label,
  videoRef,
  muted,
  recording,
  labelSide = "start",
  screenSharing,
  recordingLabel,
  screenSharingLabel
}: {
  label: string;
  videoRef: React.RefObject<HTMLVideoElement>;
  muted?: boolean;
  recording?: boolean;
  // Anchor the label to the panel's OUTER bottom corner so neither sits by the centre
  // divider under the OSD controls. In this Hebrew/RTL flex layout inline-start is the
  // RIGHT edge and inline-end the LEFT edge; the remote/officer panel (first child)
  // renders on the RIGHT and the self panel on the LEFT. So remote/officer uses "start"
  // (its outer/right edge) and self uses "end" (its outer/left edge). Logical props keep
  // the Hebrew/RTL text layout intact.
  labelSide?: "start" | "end";
  screenSharing?: boolean;
  recordingLabel?: string;
  screenSharingLabel?: string;
}) {
  const labelPos: React.CSSProperties = labelSide === "end" ? { insetInlineEnd: 8 } : { insetInlineStart: 8 };
  return (
    // flex:1 with min-width/height:0 makes each camera exactly 50% of the stage along the
    // active axis and lets it shrink instead of overflowing on small mobile viewports.
    <div style={{ flex: "1 1 50%", minWidth: 0, minHeight: 0, position: "relative", background: "#000", borderRadius: 8, overflow: "hidden" }}>
      <video ref={videoRef} autoPlay playsInline muted={muted} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      <span style={{ position: "absolute", bottom: 8, ...labelPos, color: "#fff", fontSize: 13, background: "rgba(0,0,0,.5)", padding: "2px 8px", borderRadius: 4 }}>
        {label}
      </span>
      {recording && (
        <span style={{ position: "absolute", top: 8, insetInlineEnd: 8, color: "#fff", fontSize: 12, background: "#dc2626", padding: "2px 8px", borderRadius: 999 }}>
          ● {recordingLabel}
        </span>
      )}
      {screenSharing && (
        <span style={{ position: "absolute", top: 8, insetInlineStart: 8, color: "#fff", fontSize: 12, background: "rgba(37,99,235,0.85)", padding: "2px 8px", borderRadius: 999 }}>
          ● {screenSharingLabel}
        </span>
      )}
    </div>
  );
}

// Near-transparent OSD button: floats over the video with a subtle frosted backdrop and
// hairline border so it reads on both light and dark frames; no opaque toolbar behind it.
const osdBtn: React.CSSProperties = {
  background: "rgba(15, 30, 58, 0.55)",
  color: "#fff",
  border: "1px solid rgba(255, 255, 255, 0.3)",
  borderRadius: 999,
  width: 52,
  height: 52,
  padding: 0,
  fontSize: 14,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  backdropFilter: "blur(4px)",
  WebkitBackdropFilter: "blur(4px)",
  pointerEvents: "auto"
};

const osdDanger: React.CSSProperties = {
  background: "rgba(220, 38, 38, 0.7)",
  border: "1px solid rgba(255, 255, 255, 0.4)"
};

const osdDisabled: React.CSSProperties = {
  opacity: 0.45,
  cursor: "not-allowed"
};
